import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clientPortalApi } from '../../lib/http';
import type { ClientBooking } from '../../types';

interface UpcomingBookingsSectionProps {
  expertSlug: string;
  expertName: string;
}

const UpcomingBookingsSection: React.FC<UpcomingBookingsSectionProps> = ({
  expertSlug,
  expertName,
}) => {
  const navigate = useNavigate();

  const {
    data: bookings,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['client-bookings-expert', expertSlug, 'upcoming'],
    queryFn: async () => {
      const response = await clientPortalApi.bookings.listByExpert(expertSlug, 'upcoming');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  // Don't render section if no bookings, loading, or error
  if (isLoading || isError || !bookings || bookings.length === 0) {
    return null;
  }

  const formatBookingDateTime = (booking: ClientBooking) => {
    // Use pre-formatted date from the API (in expert's timezone) to avoid browser tz conversion
    // Parse day/month/weekday from date_formatted which is like "Wednesday, March 4, 2026"
    const date = new Date(booking.starts_at);
    return {
      weekday: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: booking.expert.timezone || 'UTC' }),
      day: parseInt(booking.date_formatted.match(/\d+/)?.[0] || String(date.getDate())),
      month: date.toLocaleDateString('en-US', { month: 'short', timeZone: booking.expert.timezone || 'UTC' }),
      time: booking.time_formatted,
    };
  };

  const getStatusColor = (status: ClientBooking['status']) => {
    switch (status) {
      case 'confirmed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-xl border border-purple-100 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-purple-100/50 bg-white/60 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Your Upcoming Sessions
                </h3>
                <p className="text-xs text-gray-500">
                  with {expertName}
                </p>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">
              {bookings.length}
            </span>
          </div>
        </div>

        {/* Bookings List */}
        <div className="divide-y divide-purple-100/50">
          {bookings.slice(0, 3).map((booking) => {
            const { weekday, day, month, time } = formatBookingDateTime(booking);

            return (
              <button
                key={booking.id}
                onClick={() => navigate(`/u/bookings/${booking.id}`)}
                className="w-full px-4 py-3 flex items-center gap-4 hover:bg-white/80 transition-colors text-left group"
              >
                {/* Date Badge */}
                <div className="flex-shrink-0 w-14 h-14 bg-white rounded-xl border border-purple-100 shadow-sm flex flex-col items-center justify-center group-hover:border-purple-300 group-hover:shadow-md transition-all">
                  <span className="text-[10px] uppercase tracking-wide text-purple-600 font-medium">
                    {month}
                  </span>
                  <span className="text-lg font-bold text-gray-900 leading-tight">
                    {day}
                  </span>
                  <span className="text-[10px] text-gray-500">
                    {weekday}
                  </span>
                </div>

                {/* Booking Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {booking.service?.name || 'Appointment'}
                    </span>
                    <span
                      className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded border ${getStatusColor(
                        booking.status
                      )}`}
                    >
                      {booking.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {time}
                    </span>
                    {booking.service && (
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                          />
                        </svg>
                        {booking.service.duration_min}min
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 text-gray-300 group-hover:text-purple-500 transition-colors">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>

        {/* View All Link (if more than 3) */}
        {bookings.length > 3 && (
          <div className="px-4 py-2 bg-white/60 border-t border-purple-100/50">
            <button
              onClick={() => navigate('/u/bookings')}
              className="w-full text-center text-xs font-medium text-purple-600 hover:text-purple-700 py-1"
            >
              View all {bookings.length} bookings →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingBookingsSection;
